<template>
  <div class="pay-range-amounts-editor">
    <div
      v-if="isSourcing"
      class="pay-range-amounts-editor__sourcing"
      aria-live="polite"
      aria-busy="true"
      data-test="sourcing-banner"
    >
      <IQThinkingBanner
        state="thinking"
        variant="quiz"
        :title="sourcingConnecting ? 'Researching …' : ''"
        :caption="sourcingConnecting ? 'Sourcing Pay Range sources' : ''"
        :reasoning="reasoning"
        :phase="phase"
        :stalled="stalled"
        :started-at="sourcingStartedAt"
        collapsible
        :use-store-reasoning="false"
      />
    </div>

    <div v-if="!isSourcing && !isTimeline" class="pay-range-amounts-editor__summary">
      <span class="pay-range-amounts-editor__range">{{ rangeLabel }}</span>
    </div>

    <div
      v-if="!isSourcing"
      class="pay-range-amounts-editor__layout"
      :class="{ 'pay-range-amounts-editor__layout--sourced': showSourced }"
      data-test="amounts-layout"
    >
      <div class="pay-range-amounts-editor__rail">
        <PayRangeScale
          :orientation="isVerticalRail ? 'vertical' : 'horizontal'"
          :state="scaleState"
          :amount-unit="amountUnit"
          min-supporting-text="0 % Readiness"
          max-supporting-text="150 % Readiness"
          :readiness50-title="readinessTitle(50)"
          :readiness50-supporting-text="readinessSupporting(50)"
          :readiness100-title="readinessTitle(100)"
          :readiness100-supporting-text="readinessSupporting(100)"
          :min-error-message="isAmountInvalid ? 'Must be lower than Max' : ''"
          :max-error-message="isAmountInvalid ? 'Must be higher than Min' : ''"
        >
          <template #min-input>
            <v-text-field
              id="prd-min"
              :value="inputValue('min')"
              :type="isTimeline ? 'text' : 'number'"
              :inputmode="isTimeline ? 'decimal' : undefined"
              outlined
              dense
              hide-details
              aria-label="Minimum amount"
              :prefix="isTimeline ? undefined : currencySymbol"
              :suffix="amountUnit"
              :error="isAmountInvalid"
              placeholder="0"
              @focus="focusedField = 'min'"
              @blur="focusedField = null"
              @input="onAmountInput('min', $event)"
            />
          </template>
          <template #max-input>
            <v-text-field
              id="prd-max"
              :value="inputValue('max')"
              :type="isTimeline ? 'text' : 'number'"
              :inputmode="isTimeline ? 'decimal' : undefined"
              outlined
              dense
              hide-details
              aria-label="Maximum amount"
              :prefix="isTimeline ? undefined : currencySymbol"
              :suffix="amountUnit"
              :error="isAmountInvalid"
              placeholder="0"
              @focus="focusedField = 'max'"
              @blur="focusedField = null"
              @input="onAmountInput('max', $event)"
            />
          </template>
        </PayRangeScale>

        <div v-if="isAmountInvalid" class="pay-range-amounts-editor__alert pay-range-amounts-editor__alert--error" role="alert" data-test="max-min-alert">
          <v-icon small color="#b42318">mdi-alert-circle-outline</v-icon>
          <span>Max must exceed Min. Enter a maximum above the minimum.</span>
        </div>
      </div>

      <div v-if="showSourced || sources.length" class="pay-range-amounts-editor__pane" data-test="sourced-pane">
        <section
          v-if="showSourced"
          class="pay-range-amounts-editor__section"
          aria-labelledby="prd-suggestion-label"
          data-test="suggestion-card"
        >
          <h3 id="prd-suggestion-label" class="pay-range-amounts-editor__section-label">TRIANGULATED SUGGESTION</h3>
          <PayRangeSuggestionCard
            :suggested-min="Number(suggestion.min)"
            :suggested-max="Number(suggestion.max)"
            :median="suggestion.median == null || suggestion.median === '' ? null : Number(suggestion.median)"
            :excluded-sources="excludedSources"
            :currency="currencyCode"
            state="applied"
            @undo="emit('undo-suggestion')"
          />
        </section>

        <div v-if="!isSourcing && sources.length" class="pay-range-amounts-editor__table" data-test="market-data-table">
          <div v-if="effectiveScopeLevel" class="pay-range-amounts-editor__scope" data-test="scope-level">
            <span class="pay-range-amounts-editor__scope-label">Scope</span>
            <span class="pay-range-amounts-editor__scope-value">{{ scopeLevelLabel(effectiveScopeLevel) }}</span>
            <span v-if="fallbackDetected" class="pay-range-amounts-editor__scope-note" data-test="scope-fallback">
              fell back from {{ scopeLevelLabel(requestedScopeLevel) }}
            </span>
          </div>
          <PayRangeMarketDataTable :sources="tableSources" :currency="currencyCode" />
        </div>
      </div>
    </div>

    <div v-if="state === 'sourcing-stopped'" class="pay-range-amounts-editor__alert pay-range-amounts-editor__alert--neutral" role="status" data-test="sourcing-stopped-alert">
      <v-icon small color="#344054">mdi-information-outline</v-icon>
      <span>Sourcing stopped. No market data was applied.</span>
    </div>

    <div v-else-if="state === 'cannot-source'" class="pay-range-amounts-editor__alert pay-range-amounts-editor__alert--error" role="alert" data-test="cannot-source-alert">
      <v-icon small color="#b42318">mdi-alert-circle-outline</v-icon>
      <span>Cannot source market data for this range.</span>
      <div class="pay-range-amounts-editor__alert-actions">
        <button type="button" class="pay-range-amounts-editor__link" @click="emit('try-again')">Try Again</button>
        <button type="button" class="pay-range-amounts-editor__link" @click="emit('enter-manually')">Enter Range Manually</button>
      </div>
    </div>

    <div v-else-if="state === 'timed-out'" class="pay-range-amounts-editor__alert pay-range-amounts-editor__alert--error" role="alert" data-test="timed-out-alert">
      <v-icon small color="#b42318">mdi-clock-alert-outline</v-icon>
      <span>Market data retrieval timed out.</span>
      <div class="pay-range-amounts-editor__alert-actions">
        <button type="button" class="pay-range-amounts-editor__link" @click="emit('try-again')">Try Again</button>
        <button type="button" class="pay-range-amounts-editor__link" @click="emit('enter-manually')">Enter Range Manually</button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from "vue";
import IQThinkingBanner from "@components/IQThinkingBanner/IQThinkingBanner.vue";
import PayRangeScale from "@components/PayRangeScale/PayRangeScale.vue";
import PayRangeSuggestionCard from "@components/PayRangeSuggestionCard/PayRangeSuggestionCard.vue";
import PayRangeMarketDataTable from "@components/PayRangeMarketDataTable/PayRangeMarketDataTable.vue";
import { formatPayRangeAmount, formatPayRangeInputAmount } from "@components/utils/formatPayRangeAmount";
import { rangePenetration, readinessAmount } from "./payRangeReadiness";

const props = defineProps({
  minAmount: { type: [Number, String], default: null },
  maxAmount: { type: [Number, String], default: null },
  currencySymbol: { type: String, default: "$" },
  currencyCode: { type: String, default: "USD" },
  decimalPlaces: { type: Number, default: 2 },
  /** ANNUAL | MONTHLY | HOURLY — sets the unit shown on the readiness scale. */
  payBasis: { type: String, default: "ANNUAL" },
  /** 'default' | 'max-must-exceed-min' | 'iq-suggestion' | 'sourcing' | 'cannot-source' | 'timed-out' | 'sourcing-stopped' */
  state: { type: String, default: "default" },
  suggestion: { type: Object, default: null },
  sources: { type: Array, default: () => [] },
  requestedScopeLevel: { type: String, default: null },
  effectiveScopeLevel: { type: String, default: null },
  /**
   * 'stacked' | 'timeline'. 'timeline' lays Min, 50%, 100% and Max out horizontally with no range heading,
   * and shows the inputs with thousands separators and no currency prefix.
   */
  layout: { type: String, default: "stacked" },
  /** Streamed IQ narrative while `state` is 'sourcing' (empty until the first stream event). */
  reasoning: { type: String, default: "" },
  /** Current sourcing phase label while `state` is 'sourcing'. */
  phase: { type: String, default: "" },
  /** True when the sourcing stream has been silent for too long. */
  stalled: { type: Boolean, default: false },
  /** Timestamp (ms) the sourcing run started, so the banner timer follows the run. */
  sourcingStartedAt: { type: Number, default: null },
});

const emit = defineEmits([
  "update:minAmount",
  "update:maxAmount",
  "try-again",
  "enter-manually",
  "undo-suggestion",
]);

const isTimeline = computed(() => props.layout === "timeline");

// Two-column sourced state: only while IQ has delivered a suggestion (stays through a Max<=Min edit so the layout does not jump).
const showSourced = computed(
  () => !!props.suggestion && ["iq-suggestion", "max-must-exceed-min"].includes(props.state)
);
const isVerticalRail = computed(() => (isTimeline.value ? showSourced.value : true));

const isIncludedSource = (source) => !source.status || String(source.status).toLowerCase() === "included";

const tableSources = computed(() =>
  props.sources.map((source) => ({
    name: source.name || source.source || "—",
    min: Number(source.min),
    median: source.median == null || source.median === "" ? null : Number(source.median),
    max: Number(source.max),
    asOf: source.data_as_of || source.dataAsOf || null,
    status: isIncludedSource(source) ? "included" : "outlier",
  }))
);

const excludedSources = computed(() =>
  props.sources
    .filter((source) => !isIncludedSource(source))
    .map((source) => ({
      name: source.name || source.source,
      reason: source.exclusion_reason || source.exclusionReason || "excluded",
    }))
);
const focusedField = ref(null);

function inputValue(field) {
  const value = field === "min" ? props.minAmount : props.maxAmount;
  if (!isTimeline.value) return value;
  if (focusedField.value === field) return value == null ? "" : String(value);
  return formatPayRangeInputAmount(value, props.decimalPlaces);
}

function onAmountInput(field, raw) {
  let value = raw;
  if (isTimeline.value) {
    const [whole, ...rest] = String(raw ?? "").replace(/[^\d.]/g, "").split(".");
    value = rest.length ? `${whole}.${rest.join("")}` : whole;
  }
  emit(field === "min" ? "update:minAmount" : "update:maxAmount", value);
}

const isAmountInvalid = computed(() => props.state === "max-must-exceed-min");
const isSourcing = computed(() => props.state === "sourcing");
const sourcingConnecting = computed(() => !props.phase && !props.reasoning);

const scaleState = computed(() => (isAmountInvalid.value ? "error" : "default"));

const PAY_BASIS_PERIOD = { ANNUAL: "Year", MONTHLY: "Month", HOURLY: "Hour" };
const payPeriod = computed(() => PAY_BASIS_PERIOD[String(props.payBasis).toUpperCase()] || "Year");
const amountUnit = computed(() => `${props.currencyCode}/${payPeriod.value}`);

function readinessAt(readiness) {
  return readinessAmount(props.minAmount, props.maxAmount, readiness, props.decimalPlaces);
}

function readinessTitle(readiness) {
  const value = readinessAt(readiness);
  const amount = value == null ? "—" : formatAmount(value);
  return `${amount} / ${payPeriod.value.toLowerCase()}`;
}

function readinessSupporting(readiness) {
  const penetration = readinessAt(readiness) == null ? "—" : rangePenetration(readiness);
  return `${readiness}% Readiness (${penetration}% Range Penetration)`;
}

const fallbackDetected = computed(
  () =>
    !!props.requestedScopeLevel &&
    !!props.effectiveScopeLevel &&
    String(props.requestedScopeLevel).toUpperCase() !== String(props.effectiveScopeLevel).toUpperCase()
);

function scopeLevelLabel(value) {
  if (value == null || value === "") return "—";
  const map = {
    COUNTRY: "Country-wide",
    SUB_REGION: "Sub-region",
    REGION: "Region",
    GLOBAL: "Global",
    METRO: "Metro",
  };
  return map[String(value).toUpperCase()] || String(value);
}

const rangeLabel = computed(() => {
  const min = props.minAmount != null && props.minAmount !== "" ? formatPayRangeAmount(props.minAmount) : "—";
  const max = props.maxAmount != null && props.maxAmount !== "" ? formatPayRangeAmount(props.maxAmount) : "—";
  return `${props.currencySymbol}${min} – ${props.currencySymbol}${max}`;
});

function formatAmount(value) {
  if (value == null || value === "") return "—";
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: props.currencyCode || "USD",
      minimumFractionDigits: props.decimalPlaces,
      maximumFractionDigits: props.decimalPlaces,
    }).format(n);
  } catch {
    return `${props.currencySymbol}${n.toFixed(props.decimalPlaces)}`;
  }
}
</script>

<style scoped>
.pay-range-amounts-editor {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.pay-range-amounts-editor__summary {
  display: flex;
  align-items: baseline;
  gap: 12px;
}
.pay-range-amounts-editor__range {
  font-family: Inter, sans-serif;
  font-weight: 600;
  font-size: 16px;
  line-height: 20px;
  color: #101828;
}
.pay-range-amounts-editor__sourcing {
  width: 100%;
}
.pay-range-amounts-editor__alert {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px 12px;
  border-radius: 8px;
  font-family: Inter, sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
}
.pay-range-amounts-editor__alert--error {
  background-color: #fef2f2;
  border: 1px solid #fecaca;
  color: #b42318;
}
.pay-range-amounts-editor__alert--neutral {
  background-color: #f2f4f7;
  border: 1px solid #eaecf0;
  color: #344054;
}
.pay-range-amounts-editor__alert-actions {
  display: flex;
  gap: 12px;
  margin-left: auto;
  align-items: center;
  flex-shrink: 0;
}
.pay-range-amounts-editor__link {
  background: none;
  border: none;
  padding: 0;
  font-family: Inter, sans-serif;
  font-weight: 600;
  font-size: 14px;
  line-height: 20px;
  color: #6941c6;
  cursor: pointer;
}
.pay-range-amounts-editor__link:hover {
  text-decoration: underline;
}
.pay-range-amounts-editor__layout {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.pay-range-amounts-editor__layout--sourced {
  display: grid;
  grid-template-columns: minmax(0, 454fr) minmax(0, 1010fr);
  gap: 24px;
  align-items: start;
}
.pay-range-amounts-editor__rail,
.pay-range-amounts-editor__pane {
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
}
.pay-range-amounts-editor__section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.pay-range-amounts-editor__section-label {
  margin: 0;
  font-family: Inter, sans-serif;
  font-weight: 600;
  font-size: 12px;
  line-height: 18px;
  letter-spacing: 0.04em;
  color: #475467;
}
@media (max-width: 960px) {
  .pay-range-amounts-editor__layout--sourced {
    grid-template-columns: minmax(0, 1fr);
  }
}
.pay-range-amounts-editor__table {
  min-width: 0;
}
.pay-range-amounts-editor__scope {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  border: 1px solid #eaecf0;
  border-radius: 8px;
  margin-bottom: 8px;
  background-color: #f9fafb;
  font-family: Inter, sans-serif;
  font-size: 13px;
  line-height: 18px;
}
.pay-range-amounts-editor__scope-label {
  font-weight: 500;
  color: #475467;
}
.pay-range-amounts-editor__scope-value {
  font-weight: 600;
  color: #344054;
}
.pay-range-amounts-editor__scope-note {
  font-weight: 400;
  color: #6941c6;
}
</style>
